<template>
  <section class="product-list">
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <b-select
              v-model="selectedArea"
              :loading="loadingAreas"
              :placeholder="$t('message.please_select') + $t('attributes.area')"
              @input="onSelectArea"
            >
              <option value="">
                {{ $t('attributes.all_area') }}
              </option>
              <option
                v-for="option in areas"
                :value="option"
                :key="option.code"
              >
                {{ !option.status ? '[下架]' : '' }}
                {{ option.name }}
              </option>
            </b-select>
          </b-field>
          <b-field>
            <b-select
              v-model="selectedCatalog"
              :loading="loadingAreas"
              :disabled="catalogs.length <= 0"
              :placeholder="
                $t('message.please_select') + $t('attributes.catalog')
              "
              @input="onSelectCatalog"
            >
              <option value="">全部類別</option>
              <option
                v-for="option in catalogs"
                :value="option"
                :key="option.catalog_no"
              >
                {{ !option.status ? '[下架]' : '' }}
                {{ option.name }}
              </option>
            </b-select>
          </b-field>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.activation_status') }}
              </span>
            </p>
            <b-select v-model="filterStatus" @input="onFilterStatus">
              <option value="all">{{ $t('attributes.all') }}</option>
              <option value="true">{{ $t('attributes.shelve_on') }}</option>
              <option value="false">{{ $t('attributes.shelve_off') }}</option>
            </b-select>
          </b-field>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.product_type') }}
              </span>
            </p>
            <b-select v-model="filterType" @input="onFilterStatus">
              <option value="">{{ $t('attributes.all') }}</option>
              <option
                v-for="(type, idx) in productTypes"
                :key="idx"
                :value="type.value"
              >
                {{ type.name }}
              </option>
            </b-select>
          </b-field>
          <b-field>
            <b-input
              v-model="searchCode"
              :placeholder="
                $t('attributes.search') + $t('attributes.product_code')
              "
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
          <b-field>
            <b-input
              v-model="searchName"
              :placeholder="$t('attributes.search') + $t('attributes.name')"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop" style="width: 20%;">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            id="product-export"
            style="display:none;"
            @click="onExport"
          >
            <span>匯出表格</span>
          </b-button>
          <b-button
            :loading="sending"
            title="上架團別排序Product.Sort寫入團別分類排序CatalogProducts.Sort"
            class="is-primary"
            id="catalog-product-sort"
            style="display:none;"
            @click="setCatalogProductSort"
          >
            <span>寫入分類團別排序</span>
          </b-button>
          <b-button
            :loading="sending"
            class="is-danger"
            id="product-off"
            style="display:none;"
            @click="setProductOff"
          >
            <span>無效團別下架</span>
          </b-button>
          <b-button
            :loading="sending"
            class="is-primary"
            icon-left="plus"
            @click="isCreateModalActive = true"
          >
            <span>{{ $t('attributes.add') + $t('attributes.product') }}</span>
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :selected.sync="selectedItem"
      :loading="isLoading"
      :hoverable="true"
      :narrowed="true"
      :mobile-cards="true"
      :row-class="rowClass"
      scrollable
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :current-page="parseInt(page)"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
      @select="onSelected"
    >
      <b-table-column
        field="code"
        :label="$t('attributes.product_code')"
        width="120"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.code }}
      </b-table-column>

      <b-table-column
        field="type"
        :label="$t('attributes.product_type')"
        width="120"
        sortable
        centered
        v-slot="props"
      >
        {{ ProductType(props.row.type) }}
        {{ props.row.external_url ? '(外連)' : '' }}
      </b-table-column>

      <b-table-column
        field="shelve_on"
        :label="$t('attributes.activation_status')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        <b-tag
          :type="props.row.shelve_on ? 'is-info' : 'is-danger'"
          size="is-medium"
        >
          {{ shelveStatus(props.row.shelve_on) }}
        </b-tag>
      </b-table-column>

      <b-table-column
        field="sort"
        :label="$t('attributes.sort')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.sort }}
      </b-table-column>

      <b-table-column
        field="days"
        :label="$t('attributes.days')"
        width="85"
        sortable
        numeric
        v-slot="props"
      >
        {{ parseInt(props.row.days) }}
      </b-table-column>

      <b-table-column
        field="name"
        :label="$t('attributes.name')"
        sortable
        v-slot="props"
      >
        <p v-html="props.row.name"></p>
      </b-table-column>

      <b-table-column
        field="travel_group_valid_count"
        :label="$t('attributes.travel_group_count')"
        width="100"
        sortable
        numeric
        centered
        v-slot="props"
      >
        <p>
          {{ props.row.travel_group_valid_count }}/{{
            props.row.travel_group_count
          }}
        </p>
      </b-table-column>

      <b-table-column
        field="updated_at"
        :label="$t('attributes.modify_date')"
        width="200"
        sortable
        centered
        v-slot="props"
      >
        {{ formatDateTime(props.row.updated_at) }}
      </b-table-column>

      <b-table-column
        :label="$t('attributes.product_cover')"
        width="80"
        centered
        v-slot="props"
      >
        <a
          :href="productImageUrl(props.row.picture4)"
          @click.prevent="onClickImage(props.row.picture4)"
        >
          <figure class="image">
            <img
              :src="productImageUrl(props.row.picture4)"
              :alt="$t('attributes.product_cover')"
              style="width: 64px;"
            />
          </figure>
        </a>
      </b-table-column>
      <b-table-column
        :label="$t('attributes.route_picture')"
        width="80"
        centered
        v-slot="props"
      >
        <a
          :href="productImageUrl(props.row.picture2)"
          @click.prevent="onClickImage(props.row.picture2)"
        >
          <figure class="image">
            <img
              :src="productImageUrl(props.row.picture2)"
              :alt="$t('attributes.route_picture')"
              style="width: 64px;"
            />
          </figure>
        </a>
      </b-table-column>
      <b-table-column
        :label="$t('attributes.function')"
        width="80"
        centered
        v-slot="props"
      >
        <div class="buttons">
          <b-button type="is-danger" @click.prevent="openCloneModel(props.row)">
            {{ $t('attributes.save_as') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <hr class="my-2" />

    <section id="item">
      <edit-form
        v-if="selectedItem"
        :code="selectedItem.code"
        :loading.sync="loadingItem"
        @updated="onUpdated"
        @deleted="onDeleted"
      />
    </section>

    <b-modal
      :active.sync="isCreateModalActive"
      :can-cancel="false"
      scroll="keep"
      has-modal-card
    >
      <create-step-form
        ref="createStepForm"
        :is-sending="sending"
        @onSend="onCreate"
        @onCompleted="onCreateCompleted"
      />
    </b-modal>

    <b-modal
      :active.sync="isCloneModalActive"
      :can-cancel="['escape', 'x']"
      scroll="keep"
      has-modal-card
    >
      <clone-form
        :product="selectedClone"
        :is-sending="sending"
        @onSend="onClone"
      />
    </b-modal>

    <b-modal :active.sync="isImageModalActive">
      <p class="image">
        <img :src="selectedImageUrl" alt="" />
      </p>
    </b-modal>

    <b-loading :active="isLoading" :is-full-page="true" :can-cancel="false" />
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import { AREA_API, PRODUCTS_API } from '@/utils/apis'
import CreateStepForm from '@/components/products/CreateStepForm'
import CloneForm from '@/components/products/CloneForm'
import EditForm from '@/components/products/Form'
import notifyService from '@/services/notifyService'

export default {
  name: 'ProductList',
  components: {
    CreateStepForm,
    CloneForm,
    EditForm
  },

  data() {
    const { query } = this.$route
    const productTypes = this.GetProductTypes()

    return {
      productTypes,
      loading: false,
      loadingItems: false,
      total: 0,
      items: [],
      selectedItem: null,
      loadingItem: false,

      filterType: query.type || '',
      filterStatus: query.status || 'true',
      searchCode: query.code || '',
      searchName: query.search || '',
      sortField: query.sort || 'travel_group_valid_count',
      sortOrder: query.order || 'asc',
      defaultSortOrder: 'asc',
      page: query.page || 1,
      perPage: query.page_size || 10,

      loadingAreas: false,
      areas: [],
      catalogs: [],
      selectedArea: null,
      selectedAreaCode: query.ac || null,
      selectedCatalog: null,
      selectedCatalogNo: query.cn || null,

      isCloneModalActive: false,
      selectedClone: null,

      isCreateModalActive: false,
      sending: false,

      isImageModalActive: false,
      selectedImageUrl: null,

      empCode: '',
      adminEmpCodes: [462, 26, 567]
    }
  },
  computed: {
    isLoading() {
      return (
        this.loading ||
        this.loadingItems ||
        this.loadingAreas ||
        this.loadingItem
      )
    }
  },
  mounted() {
    this.loadData()
  },
  beforeRouteUpdate(to, from, next) {
    if (to.name === from.name) {
      this.getQuery(to.query)
      this.loadProducts()
    }

    next()
  },
  methods: {
    async loadData() {
      this.loading = true
      await this.loadAreas()
      await this.loadProducts()
      this.setProductOffButton()
      this.loading = false
    },
    async setProductOff() {
      if (!confirm('確定下架無效團別?')) {
        return
      }
      try {
        const api = `${PRODUCTS_API}/off`

        this.loadingItems = true
        await this.axios.put(api)

        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.loadingItems = false
      }
    },
    setProductOffButton() {
      const empCode = this.$store.getters.empCode
      if (this.adminEmpCodes.indexOf(parseInt(empCode)) !== -1) {
        document.getElementById('product-off').style.display = 'block'
        document.getElementById('catalog-product-sort').style.display = 'block'
        //document.getElementById('product-export').style.display = 'block'
      }
    },
    async setCatalogProductSort() {
      if (
        !confirm(
          '確定要把上架團別排序Product.Sort寫入團別分類排序CatalogProducts.Sort?'
        )
      ) {
        return
      }
      try {
        const api = `${PRODUCTS_API}/set-sort`

        this.loadingItems = true
        await this.axios.get(api)

        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.loadingItems = false
      }
    },
    async onExport() {
      try {
        const api = `${PRODUCTS_API}/export`
        const config = {
          headers: { Accept: 'application/vnd.ms-excel' },
          responseType: 'blob'
        }

        this.loadingItems = true
        const response = await this.axios.get(api, config)
        const date = this.$dayjs().format('YYYYMMDDHHmmss')
        const fileName = `products_${date}`

        const blob = new Blob([response.data], { type: response.data.type })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        notifyService.error('下載失敗', e)
      } finally {
        this.loadingItems = false
      }
    },
    async loadAreas() {
      try {
        this.loadingAreas = true
        const res = await this.axios.get(AREA_API)
        const areas = res.data.data
        this.areas = areas.sort(a => a.sort)
        this.catalogs = []
        this.selectedArea = null
        this.selectedCatalog = null

        if (this.selectedAreaCode) {
          this.selectedArea = areas.find(a => a.code === this.selectedAreaCode)
          this.catalogs = this.selectedArea
            ? this.selectedArea.catalogs.sort(c => c.sort)
            : []
        }
        if (this.selectedCatalogNo) {
          this.selectedCatalog = this.catalogs.find(
            c => c.catalog_no === this.selectedCatalogNo
          )
        }
      } catch (e) {
        this.areas = []
        this.catalogs = []
        this.selectedArea = null
        this.selectedAreaCode = null
        this.selectedCatalog = null
        this.selectedCatalogNo = null

        notifyService.fetch_error('area', e)
      } finally {
        this.loadingAreas = false
      }
    },
    async loadProducts() {
      try {
        const api = PRODUCTS_API

        const params = {
          type: this.filterType,
          active: this.filterStatus === 'all' ? '' : this.filterStatus,
          area_code: this.selectedAreaCode,
          catalog_no: this.selectedCatalogNo,
          search_code: this.searchCode,
          search: this.searchName,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('product', e)
      } finally {
        this.loadingItems = false
      }
    },
    getQuery(query) {
      this.filterType = query.type || ''
      this.filterStatus = query.status || 'true'
      this.searchCode = query.code || ''
      this.searchName = query.search || ''
      this.sortField = query.sort || 'travel_group_valid_count'
      this.sortOrder = query.order || 'asc'
      this.page = query.page || 1
      this.perPage = query.page_size || 10

      this.selectedArea = null
      this.selectedCatalog = null
      this.selectedAreaCode = query.ac || null
      this.selectedCatalogNo = query.cn || null

      if (this.selectedAreaCode) {
        this.selectedArea = this.areas.find(
          a => a.code === this.selectedAreaCode
        )
        this.catalogs = this.selectedArea
          ? this.selectedArea.catalogs.sort(c => c.sort)
          : []
      }
      if (this.selectedCatalogNo) {
        this.selectedCatalog = this.catalogs.find(
          c => c.catalog_no === this.selectedCatalogNo
        )
      }
    },
    updateQuery() {
      const query = {
        type: this.filterType,
        status: this.filterStatus,
        ac: this.selectedAreaCode,
        cn: this.selectedCatalogNo,
        code: this.searchCode,
        search: this.searchName,
        sort: this.sortField,
        order: this.sortOrder,
        page_size: this.perPage.toString(),
        page: this.page.toString()
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])
      this.$router
        .push({
          name: this.$route.name,
          query
        })
        .catch(() => {})
    },

    onSelected() {
      this.$scrollTo(`#item`, { offset: -60 })
    },
    onFilterStatus() {
      this.updateQuery()
    },
    onSelectArea(option) {
      this.selectedArea = option
      this.selectedAreaCode = option ? option.code : null
      this.catalogs = option ? option.catalogs : []
      this.selectedCatalog = null
      this.selectedCatalogNo = null

      this.updateQuery()
    },
    onSelectCatalog(option) {
      this.selectedCatalog = option
      this.selectedCatalogNo = option ? option.catalog_no : null

      this.updateQuery()
    },

    onFilter: debounce(function() {
      this.page = 1
      this.updateQuery()
    }, 500),
    onPageChange(page) {
      this.page = page
      this.updateQuery()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.updateQuery()
    },

    async onCreate(item) {
      try {
        const api = `${PRODUCTS_API}`

        this.sending = true
        const result = await this.axios.post(api, item)
        const product = result.data.data

        notifyService.success(this.$t('attributes.add_succeed'))
        this.$refs.createStepForm.onCreated(product)
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async onCreateCompleted() {
      await this.loadProducts()

      this.isCreateModalActive = false
    },

    openCloneModel(item) {
      this.selectedClone = item
      this.isCloneModalActive = true
    },
    async onClone(code, item) {
      try {
        this.sending = true

        const api = `${PRODUCTS_API}/${code}/clone`
        await this.axios.post(api, item)
        this.isCloneModalActive = false

        notifyService.success(this.$t('attributes.add_succeed'))
        await this.loadProducts()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    onUpdated(product) {
      this.items = this.items.map(g => {
        if (g.code === product.code) {
          return {
            ...product,
            travel_group_valid_count: g.travel_group_valid_count,
            travel_group_count: g.travel_group_count
          }
        } else {
          return g
        }
      })
      this.selectedItem = this.items.find(item => item.code === product.code)
    },
    onDeleted(code) {
      this.items = this.items.filter(i => i.code !== code)
      this.selectedItem = null
    },

    onClickImage(url) {
      this.isImageModalActive = true
      this.selectedImageUrl = this.productImageUrl(url)
    },
    rowClass(item) {
      return {
        'has-background-danger-light': !item.is_valid,
        'has-text-dark': !item.is_valid,
        'taken-off': item.shelve_on,
        expired: item.is_expired,
        fulled: item.is_full
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;
}
</style>
